import { useQuery } from '@apollo/client';

import {
  ProductType,
  PurchaseWebCampaignType,
} from '@xing-com/crate-common-graphql-types';
import { redirectTo } from '@xing-com/crate-core-router';
import { GetCampaignDocument } from '@xing-com/crate-upsell-graphql';
import type { GetCampaignQuery } from '@xing-com/crate-upsell-graphql';
import {
  usePayOrConsentCampaignMid,
  useProductParameter,
} from '@xing-com/crate-upsell-utils';

import { useLanguage } from './use-language';

/**
 * Get prices to show contract options user can buy.
 * if user already member then gets redirect to features page
 *
 * @returns {object} { loading, status, product, campaign }
 */
export function useCampaign(): {
  loading: boolean;
  status:
    | 'SUCCESS'
    | 'NO_PRODUCT_PARAMETER'
    | 'NO_CAMPAIGN_DATA'
    | 'LOADING'
    | 'NETWORK_ERROR';
  product: ProductType;
  campaign: GetCampaignQuery['results'] | null;
} {
  const { product } = useProductParameter();
  const { loadingLanguage, language } = useLanguage();
  const mid = usePayOrConsentCampaignMid();

  const {
    loading: loadingCampaign,
    error: campaignError,
    data,
  } = useQuery(GetCampaignDocument, {
    skip: !product || loadingLanguage,
    variables: { mid, product, language },
  });

  if (loadingCampaign || loadingLanguage)
    return { loading: true, status: 'LOADING', product, campaign: null };

  if (campaignError) {
    return {
      loading: false,
      status: 'NETWORK_ERROR',
      product,
      campaign: null,
    };
  }

  // user is already member
  if (!data || !data.results) {
    redirectTo(`/${product}/features`);
    return {
      loading: false,
      status: 'NO_CAMPAIGN_DATA',
      product,
      campaign: null,
    };
  }

  if (data?.results?.campaignType === PurchaseWebCampaignType.Revoke) {
    return {
      loading: false,
      status: 'SUCCESS',
      product: product ?? ProductType.Premium,
      campaign: data.results,
    };
  }

  return {
    loading: false,
    status: 'SUCCESS',
    product: product ?? ProductType.Premium,
    campaign: data.results,
  };
}
